<template>
  <Dialog :visible="visible" @update:visible="$emit('update:visible', $event)" :header="'Asistente: ' + (promo ? promo.nombre : 'Promo Dinámica')" :modal="true" :style="{ width: '500px' }" class="p-fluid">
    <div v-if="loading" class="flex justify-content-center p-4">
      <ProgressSpinner />
    </div>
    
    <div v-else-if="promo" class="flex flex-column gap-3">
      <div class="text-center">
        <img v-if="promo.imagen_pos" :src="promo.imagen_pos" style="max-height: 120px;" class="border-round shadow-1 mb-3" />
        <p class="text-600 m-0">Selecciona los productos para armar tu combo y aprovechar el descuento.</p>
      </div>

      <div v-for="(condicion, index) in promo.conditions" :key="'cond_'+index" class="bg-gray-50 p-3 border-round mb-2 shadow-1">
        <label class="font-bold block mb-2 text-900 text-lg">
          Paso {{ index + 1 }}: Elige {{ condicion.cantidad_minima || 1 }} {{ obtenerNombreTarget(condicion) }}
        </label>
        
        <div v-for="n in (condicion.cantidad_minima || 1)" :key="'cond_sel_'+index+'_'+n" class="mb-2">
          <Dropdown 
            v-model="seleccionesCondiciones[index + '_' + n]" 
            :options="obtenerProductosParaCondicion(condicion)" 
            dataKey="id" 
            optionLabel="nombre" 
            :placeholder="'Selecciona la opción ' + n" 
            class="w-full p-2" 
            filter 
          >
            <template #option="slotProps">
              <div class="flex align-items-center">
                <span class="font-bold">{{ slotProps.option.nombre }}</span>
                <span class="ml-auto text-green-600 font-bold" v-if="obtenerPrecioReal(slotProps.option) > 0">
                  Bs. {{ obtenerPrecioReal(slotProps.option).toFixed(2) }}
                </span>
              </div>
            </template>
          </Dropdown>
        </div>
      </div>

      <div v-for="(accion, index) in promo.actions" :key="'acc_'+index" class="bg-green-50 p-3 border-round mb-2 shadow-1">
        <label class="font-bold block mb-2 text-900 text-lg">
          Paso {{ promo.conditions.length + index + 1 }}: Elige {{ accion.max_aplicaciones_carrito || 1 }} Beneficio(s) ({{ obtenerNombreTargetAccion(accion) }})
        </label>
        
        <div v-for="n in (accion.max_aplicaciones_carrito || 1)" :key="'acc_sel_'+index+'_'+n" class="mb-2">
          <Dropdown 
            v-model="seleccionesAcciones[index + '_' + n]" 
            :options="obtenerProductosParaAccion(accion)" 
            dataKey="id" 
            optionLabel="nombre" 
            :placeholder="'Selecciona el beneficio ' + n" 
            class="w-full p-2" 
            filter 
          >
            <template #option="slotProps">
              <div class="flex align-items-center">
                <span class="font-bold">{{ slotProps.option.nombre }}</span>
                <div class="ml-auto flex flex-column align-items-end">
                  <span class="text-500 line-through text-sm" v-if="obtenerPrecioReal(slotProps.option) > 0">
                    Bs. {{ obtenerPrecioReal(slotProps.option).toFixed(2) }}
                  </span>
                  <span class="text-green-600 font-bold">
                    Bs. {{ (obtenerPrecioReal(slotProps.option) - (obtenerPrecioReal(slotProps.option) * (accion.valor_descuento / 100))).toFixed(2) }}
                  </span>
                </div>
              </div>
            </template>
          </Dropdown>
        </div>
      </div>

    </div>        

    <template #footer>
      <div class="flex justify-content-between align-items-center w-full border-top-1 surface-border pt-4 mt-2">
        <div class="text-left" v-if="precioNormal > 0">
           <span class="text-500 line-through text-sm block">Precio Normal: Bs. {{ precioNormal.toFixed(2) }}</span>
           <span class="text-xl font-bold text-green-600 block">Total Combo: Bs. {{ precioTotal.toFixed(2) }}</span>
        </div>
        <div class="flex gap-3">
          <Button label="Cancelar" icon="pi pi-times" class="p-button-text p-button-secondary" @click="$emit('cancelar')" />
          <Button label="Agregar al Carrito" icon="pi pi-check" class="p-button-success shadow-2" @click="confirmar" :disabled="!todoSeleccionado" />
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script>
import PromotionService from '@/service/PromotionService';

export default {
  name: 'ZapiModalSmartCombo',
  props: {
    visible: { type: Boolean, default: false },
    promoId: { type: Number, default: null },
    catalogoTotal: { type: Array, default: () => [] },
    grupos: { type: Array, default: () => [] },
    canalVentaId: { type: Number, default: 1 }
  },
  emits: ['update:visible', 'confirmar', 'cancelar'],
  data() {
    return {
      loading: false,
      promo: null,
      // Object arrays to hold selections based on unique keys
      seleccionesCondiciones: {},
      seleccionesAcciones: {},
      promotionService: new PromotionService()
    }
  },
  watch: {
    visible(newVal) {
      if (newVal && this.promoId) {
        this.cargarPromo();
      } else {
        this.promo = null;
        this.seleccionesCondiciones = {};
        this.seleccionesAcciones = {};
      }
    }
  },
  computed: {
    /**
     * Determina el canal de venta a utilizar para los cálculos de precios.
     * Si la promoción tiene canales permitidos, verifica si el canal actual está permitido.
     * Si no lo está, utiliza el primer canal permitido por defecto.
     * @returns {Number} ID del canal de venta a utilizar.
     */
    canalParaCalculo() {
      if (this.promo && this.promo.canales_id && this.promo.canales_id.length > 0) {
        if (!this.promo.canales_id.includes(this.canalVentaId)) {
          return this.promo.canales_id[0];
        }
      }
      return this.canalVentaId;
    },
    /**
     * Calcula la cantidad total de selecciones (dropdowns) requeridas para cumplir
     * con todas las condiciones de la promoción.
     * @returns {Number} Cantidad total de condiciones obligatorias.
     */
    totalCondicionesRequeridas() {
      if (!this.promo || !this.promo.conditions) return 0;
      return this.promo.conditions.reduce((acc, c) => acc + (c.cantidad_minima || 1), 0);
    },
    /**
     * Calcula la cantidad total de selecciones (dropdowns) requeridas para los beneficios (acciones)
     * de la promoción.
     * @returns {Number} Cantidad total de acciones u opciones de beneficio a seleccionar.
     */
    totalAccionesRequeridas() {
      if (!this.promo || !this.promo.actions) return 0;
      return this.promo.actions.reduce((acc, a) => acc + (a.max_aplicaciones_carrito || 1), 0);
    },
    /**
     * Verifica si el usuario ha completado todas las selecciones obligatorias
     * (tanto condiciones como acciones) para habilitar el botón de confirmar.
     * @returns {Boolean} true si todo está seleccionado correctamente, false en caso contrario.
     */
    todoSeleccionado() {
      if (!this.promo) return false;
      const condLlenas = Object.values(this.seleccionesCondiciones).filter(v => v != null).length;
      const condOk = condLlenas === this.totalCondicionesRequeridas;
      // Para acciones, obligamos a que seleccionen el limite. (Puede ajustarse en el debate, pero por ahora estricto)
      const accLlenas = Object.values(this.seleccionesAcciones).filter(v => v != null).length;
      const accOk = this.promo.actions ? (accLlenas === this.totalAccionesRequeridas) : true;
      return condOk && accOk;
    },
    /**
     * Calcula el precio final del combo, aplicando los descuentos de las acciones seleccionadas.
     * Utiliza el precio correspondiente al canal de venta actual.
     * @returns {Number} Monto total a pagar por el combo con descuentos.
     */
    precioTotal() {
      let total = 0;
      Object.values(this.seleccionesCondiciones).forEach(s => {
        if (s && s.precioBase) total += this.obtenerPrecioReal(s);
      });
      // Para acciones, necesitamos saber a qué descuento aplica cada seleccion.
      // Como las llaves de seleccionesAcciones son 'index_n', parseamos el index.
      Object.keys(this.seleccionesAcciones).forEach(key => {
        const s = this.seleccionesAcciones[key];
        if (s && s.precioBase) {
          const accionIndex = parseInt(key.split('_')[0]);
          const accion = this.promo.actions[accionIndex];
          const pReal = this.obtenerPrecioReal(s);
          const descuento = (pReal * (accion.valor_descuento / 100));
          total += (pReal - descuento);
        }
      });
      return total;
    },
    /**
     * Calcula el precio original del combo sin aplicar ningún descuento.
     * Sirve para mostrar el precio tachado en la interfaz y resaltar el ahorro.
     * @returns {Number} Monto total del combo sin descuentos.
     */
    precioNormal() {
      let total = 0;
      Object.values(this.seleccionesCondiciones).forEach(s => {
        if (s && s.precioBase) total += this.obtenerPrecioReal(s);
      });
      Object.values(this.seleccionesAcciones).forEach(s => {
        if (s && s.precioBase) total += this.obtenerPrecioReal(s);
      });
      return total;
    }
  },
  methods: {
    /**
     * Obtiene el precio dinámico de un producto basado en el canal de venta actual
     * (por ejemplo: pv2 para Yango, pv3 para PedidosYa).
     * Incorpora una validación para evitar que el producto sea gratis si su precio especial es "0.00".
     *
     * @param {Object} producto - Objeto del producto del catálogo.
     * @returns {Number} El precio aplicable para el canal, o el precio base si el canal no tiene precio configurado.
     */
    obtenerPrecioReal(producto) {
      let precio = parseFloat(producto.precioBase || 0);
      let precioCanal = 0;
      if (this.canalParaCalculo === 3) precioCanal = parseFloat(producto.pv2);
      else if (this.canalParaCalculo === 2) precioCanal = parseFloat(producto.pv3);
      else if (this.canalParaCalculo === 4) precioCanal = parseFloat(producto.pv4);
      
      return (precioCanal > 0) ? precioCanal : precio;
    },
    /**
     * Carga los datos de la promoción desde el backend usando su ID.
     * Reinicia las selecciones del usuario para preparar el modal.
     */
    cargarPromo() {
      this.loading = true;
      this.promotionService.getPromotion(this.promoId).then(res => {
        this.promo = res.data || res;
        this.seleccionesCondiciones = {};
        this.seleccionesAcciones = {};
      }).catch(err => {
        console.error(err);
        this.$toast.add({severity:'error', summary: 'Error', detail: 'No se pudo cargar la promoción.'});
        this.$emit('cancelar');
      }).finally(() => {
        this.loading = false;
      });
    },
    /**
     * Obtiene una descripción amigable (nombre del grupo o subgrupo) para mostrar en el paso de condición.
     * 
     * @param {Object} condicion - La condición configurada en la promoción.
     * @returns {String} Texto descriptivo del target (ej: "de PIZZAS TRADICIONALES").
     */
    obtenerNombreTarget(condicion) {
      if (condicion.tipo === 'grupo' || condicion.tipo === 'repetido') {
        const grupo = this.grupos.find(g => g.gru_id === condicion.target_id || g.id === condicion.target_id);
        return grupo ? `de ${grupo.gru_nombre || grupo.nombre}` : 'productos';
      }
      if (condicion.tipo === 'subgrupo') {
        for (let g of this.grupos) {
          if (g.subgrupos) {
            const sg = g.subgrupos.find(s => s.sub_id === condicion.target_id || s.id === condicion.target_id);
            if (sg) return `de ${sg.sub_nombre || sg.nombre}`;
          }
        }
        return 'productos del subgrupo';
      }
      return 'productos';
    },
    /**
     * Filtra el catálogo principal para devolver únicamente los productos que cumplen
     * con los requisitos de la condición (por grupo o subgrupo) y que no son paquetes.
     * 
     * @param {Object} condicion - La regla de condición.
     * @returns {Array} Lista de productos elegibles para el dropdown de esta condición.
     */
    obtenerProductosParaCondicion(condicion) {
      if (condicion.tipo === 'grupo' || condicion.tipo === 'repetido') {
        return this.catalogoTotal.filter(p => p.grupo_id === condicion.target_id && !p.es_smart_combo && !p.es_paquete);
      } else if (condicion.tipo === 'subgrupo') {
        return this.catalogoTotal.filter(p => p.subgrupo_id === condicion.target_id && !p.es_smart_combo && !p.es_paquete);
      }
      return this.catalogoTotal;
    },
    /**
     * Obtiene una descripción amigable para el paso del beneficio (acción).
     * Puede apuntar a grupos, subgrupos o tipos especiales como "Extras" o "Bordes".
     * 
     * @param {Object} accion - La regla de beneficio configurada en la promoción.
     * @returns {String} Texto descriptivo del target.
     */
    obtenerNombreTargetAccion(accion) {
      if (accion.aplica_a === 'grupo') {
        const grupo = this.grupos.find(g => g.gru_id === accion.target_id || g.id === accion.target_id);
        return grupo ? `de ${grupo.gru_nombre || grupo.nombre}` : 'productos';
      }
      if (accion.aplica_a === 'subgrupo') {
        for (let g of this.grupos) {
          if (g.subgrupos) {
            const sg = g.subgrupos.find(s => s.sub_id === accion.target_id || s.id === accion.target_id);
            if (sg) return `de ${sg.sub_nombre || sg.nombre}`;
          }
        }
        return 'productos del subgrupo';
      }
      if (accion.aplica_a === 'extra') return 'Extras de Pizza';
      if (accion.aplica_a === 'borde') return 'Bordes de Pizza';
      return 'productos';
    },
    /**
     * Filtra el catálogo para obtener los productos que el usuario puede elegir como beneficio,
     * respetando el tipo de aplicación (grupo, subgrupo, extra, borde).
     * 
     * @param {Object} accion - La regla de beneficio.
     * @returns {Array} Lista de productos elegibles para ser descontados o regalados.
     */
    obtenerProductosParaAccion(accion) {
      if (accion.aplica_a === 'grupo') {
        return this.catalogoTotal.filter(p => p.grupo_id === accion.target_id && !p.es_smart_combo && !p.es_paquete);
      } else if (accion.aplica_a === 'subgrupo') {
        return this.catalogoTotal.filter(p => p.subgrupo_id === accion.target_id && !p.es_smart_combo && !p.es_paquete);
      } else if (accion.aplica_a === 'extra') {
        return this.catalogoTotal.filter(p => p.extra && !p.es_smart_combo && !p.es_paquete);
      } else if (accion.aplica_a === 'borde') {
        return this.catalogoTotal.filter(p => p.es_borde && !p.es_smart_combo && !p.es_paquete);
      }
      return this.catalogoTotal;
    },
    /**
     * Procesa todas las selecciones hechas por el usuario, inyecta los precios según el canal
     * y los descuentos correspondientes a los beneficios, y emite el evento 'confirmar'
     * para enviar el combo armado al carrito principal.
     */
    confirmar() {
      const seleccionesConDescuento = [];
      
      Object.values(this.seleccionesCondiciones).forEach(s => {
        if (s) {
          seleccionesConDescuento.push({
            ...s, 
            precioBase: this.obtenerPrecioReal(s),
            descuento_neto: 0,
            nombre_paquete: `Combo: ${this.promo.nombre}`
          });
        }
      });
      
      Object.keys(this.seleccionesAcciones).forEach(key => {
        const s = this.seleccionesAcciones[key];
        if (s) {
          const accionIndex = parseInt(key.split('_')[0]);
          const accion = this.promo.actions[accionIndex];
          const pReal = this.obtenerPrecioReal(s);
          const descuento = (pReal * (accion.valor_descuento / 100));
          seleccionesConDescuento.push({
            ...s, 
            precioBase: pReal,
            descuento_neto: descuento,
            nombre_paquete: `Combo: ${this.promo.nombre}`
          });
        }
      });

      this.$emit('confirmar', { selecciones: seleccionesConDescuento, promo: this.promo });
    }
  }
}
</script>
